import React, {FunctionComponent, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router';
import PlatformActionsPageBlock from '../../../../components/PlatformComponents/PlatformActionsPage';
import {EPaymentRequiredType, EPlatformsNames, EStripeSubStatus, ETrialStatus} from '../../../../models/consts';
import {IError, IPaymentRequiredPoppup} from '../../../../models/inner-models';
import {downloadChatgptZip, getChatgptActions, restoreGitlabBackup,} from '../../../../store/actions';
import {useAppDispatch, useAppSelector} from '../../../../store/hooks';
import {clearBackupsState} from '../../../../store/reducers/backups';
import {
  chatgptSubscription,
  chatgptTrialStatus,
  getChatgptActionsSelector,
  getChatgptActionsTotalSelector,
  getChatgptCanRestore,
  isChatgptActionsLoadingSelector,
  isSubUser,
  roleByApp,
} from '../../../../store/selectors';
import restore from '../../../../utils/functions/restore';
import withRedirectAfterLogout from '../../../../utils/withRedirectAfterLogout';
import {IBackupRow} from '../../types';
import Swal from "sweetalert2";
import {decoratePopUpMessage} from "../../../../utils/popUpTextDecorator";
import {errorBigIcon, iconNotifySuccess} from "../../../../assets/icons/icons";

const ChatActivityPage: FunctionComponent = () => {
  const platformName = EPlatformsNames.CHATGPT;

  const [searchParams] = useSearchParams();
  const {t: translate} = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const id = searchParams.get('id');

  const chatgptActions = useAppSelector(getChatgptActionsSelector);
  const actionsTotal = useAppSelector(getChatgptActionsTotalSelector);
  const loading = useAppSelector(isChatgptActionsLoadingSelector);
  const canRestore = useAppSelector(getChatgptCanRestore);
  const trialState = useAppSelector(chatgptTrialStatus);
  const subInfo = useAppSelector(chatgptSubscription);
  const isUserSub = useAppSelector(isSubUser);
  const roleApp = useAppSelector(roleByApp);
  const isAdminRole = isUserSub && roleApp?.chatgpt === 'admin' && (trialState === 'active' || subInfo?.planName === 'Pro') || !isUserSub

  const [rows, setRows] = useState<Array<IBackupRow> | null>(null);
  const [paymentRequiredState, setPaymentRequiredState] = useState<EPaymentRequiredType>(EPaymentRequiredType.NON);
  const [paymentPoppup, setPaymentPoppup] = useState<IPaymentRequiredPoppup>({
    isOpen: false,
    platformName: platformName,
  });
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (actionsTotal && actionsTotal !== total) {
      setTotal(actionsTotal)
    }
  }, [actionsTotal])

  useEffect(() => {
    setPaymentRequiredState(
      subInfo?.status === EStripeSubStatus.ACTIVE || trialState === ETrialStatus.STARTED ?
        EPaymentRequiredType.NON :
        !subInfo ? EPaymentRequiredType.TRIAL : EPaymentRequiredType.SUB,
    );
  }, [trialState, subInfo]);

  const restoreItem = (versions: string, name: string, key: string, emails: string[]) => {
    if (paymentRequiredState === EPaymentRequiredType.NON) {
      return restore(() => onConfirmRestore(versions, name, key, emails), translate);
    } else {
      if (!isUserSub) {
        setPaymentPoppup((prev) => ({
          ...prev,
          isOpen: true,
        }));
      }
    }
  };

  const closeTrialPoppup = useCallback(() => {
    setPaymentPoppup({...paymentPoppup, isOpen: false});
  }, []);

  const onConfirmRestore = async (versionId, name, projectId, emails) => {
    if (versionId && projectId) {
      return dispatch(restoreGitlabBackup({
        items: [{id: projectId, versionId: versionId, name: name}],
        emails
      })).unwrap();
    }
  };

  const downloadZipCallback = useCallback(({versionid}) => {
    dispatch(downloadChatgptZip({s3Key: versionid}))
      .unwrap()
      .then((res) => {
        console.log(res)
        window.open(`${res.downloadUrl}`, '_blank');
      })
      .catch((err) => {
        console.log(err);
        const error = err as IError;

        if (error.status === 'success') {
          Swal.fire({
            title: translate('notifications.titles.success'),
            text: decoratePopUpMessage(`${error.message as string}. The zip download can take a few minutes depending on the size. We will send you an email once it is ready and available to download.`),
            imageUrl: iconNotifySuccess,
          });
        } else {
          Swal.fire({
            title: translate('notifications.titles.error'),
            text: decoratePopUpMessage(error.message as string),
            imageUrl: errorBigIcon,
          });
        }
      });
  }, []);

  useEffect(() => {
    !loading && chatgptActions && setRows(chatgptActions?.map((props, index) => ({...props, id: index})));
  }, [chatgptActions, loading]);

  useEffect(() => {
    if (!id) {
      navigate('/')
      return;
    }

    updList()
  }, [id]);


  useEffect(() => {
    return () => {
      dispatch(clearBackupsState());
    };
  }, [dispatch]);

  const updList = async (newPage?: number) => {
    try {
      if (!id) {
        return;
      }

      setIsLoading(true)
      await dispatch(getChatgptActions({itemId: id, perPage: 10, page: newPage || 0}))
        .unwrap()
        .catch((err) => {
          console.log(err);
          const error = err as IError;
          setIsLoading(false)

          Swal.fire({
            title: translate('notifications.titles.error'),
            text: decoratePopUpMessage(error.error as string),
            imageUrl: errorBigIcon,
          })
            .then(() => {
              navigate('/platform/dashboard/chatgpt')
            });
        });

      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
      console.log(e);
    }
  }

  return (
    <PlatformActionsPageBlock
      platformName={platformName}
      rows={rows || []}
      downloadZipCallback={downloadZipCallback}
      restoreItemCallback={restoreItem}
      closeTrialPopupCallback={closeTrialPoppup}
      loading={loading}
      total={total}
      page={page}
      setPage={(p) => {
        setPage(p)
        updList(p)
      }}
      isLoading={isLoading}
      paymentPopup={paymentPoppup}
      paymentRequiredState={paymentRequiredState}
      canRestore={canRestore}
      isAdmin={isAdminRole}
    />
  );
};

export default withRedirectAfterLogout()(ChatActivityPage);
